/**
 * R66 协议路由与版本判定（双通道：URL apilevel=40660 或 getAppInfo.app_version）
 * 依赖：App-JSBridge.js（Legacy）、App-WebContainer-Bridge-R66.js
 */
(function (global) {
  'use strict';

  var PROTOCOL_APILEVEL = 40660;
  var PROTOCOL_MIN_APP_VERSION = '4.66.00';
  var FEATURE_COMPACT_NAV_MIN_VERSION = '4.66.00';

  var _tierCache = null;
  var _tierReasonCache = null;
  var _tierPromise = null;

  function getPersistedApiLevel() {
    try {
      if (
        global.rrMallContainerQuery &&
        typeof global.rrMallContainerQuery.getPersistedQuery === 'function'
      ) {
        var q = global.rrMallContainerQuery.getPersistedQuery();
        if (q && q.apilevel != null && q.apilevel !== '') {
          return String(q.apilevel);
        }
      }
    } catch (e) {}
    return null;
  }

  function hasApilevel40660InUrl() {
    try {
      var params = new URLSearchParams(global.location.search);
      return params.get('apilevel') === String(PROTOCOL_APILEVEL);
    } catch (e) {
      return false;
    }
  }

  /** URL 或 session（首页写入）存在 apilevel=40660 时视为 R66 容器信号 */
  function hasUrlApiLevel40660() {
    if (hasApilevel40660InUrl()) return true;
    return getPersistedApiLevel() === String(PROTOCOL_APILEVEL);
  }

  function compareVersion(current, minVersion) {
    if (typeof global.compareAppVersion === 'function') {
      return global.compareAppVersion(current, minVersion) >= 0;
    }
    if (typeof global.isAppVersionAtLeast === 'function') {
      return global.isAppVersionAtLeast(current, minVersion);
    }
    return false;
  }

  function fetchAppInfo() {
    if (global.AppWebBridge && global.AppWebBridge.isBridgeReady()) {
      return global.AppWebBridge.getAppInfo();
    }
    if (typeof global.getAppInfo === 'function') {
      return global.getAppInfo();
    }
    return Promise.reject(new Error('NO_GET_APP_INFO'));
  }

  function resolveProtocolTier() {
    if (_tierCache) {
      return Promise.resolve(_tierCache);
    }
    if (_tierPromise) {
      return _tierPromise;
    }

    _tierPromise = new Promise(function (resolve) {
      if (hasUrlApiLevel40660()) {
        _tierCache = 'r66';
        _tierReasonCache = hasApilevel40660InUrl() ? 'url_apilevel' : 'session_apilevel';
        resolve('r66');
        return;
      }

      var getAppInfoFn = fetchAppInfo;
      if (!global.AppWebBridge && typeof global.getAppInfo !== 'function') {
        _tierCache = 'legacy';
        _tierReasonCache = 'fallback_legacy';
        resolve('legacy');
        return;
      }

      getAppInfoFn()
        .then(function (info) {
          var v = info && info.app_version;
          if (v != null && compareVersion(v, PROTOCOL_MIN_APP_VERSION)) {
            _tierCache = 'r66';
            _tierReasonCache = 'app_version';
            resolve('r66');
          } else {
            _tierCache = 'legacy';
            _tierReasonCache = 'fallback_legacy';
            resolve('legacy');
          }
        })
        .catch(function () {
          _tierCache = 'legacy';
          _tierReasonCache = 'fallback_legacy';
          resolve('legacy');
        });
    });

    return _tierPromise;
  }

  function getProtocolTierSync() {
    return _tierCache;
  }

  function getProtocolTierReason() {
    return _tierReasonCache;
  }

  function getAppVersion() {
    if (global.AppWebBridge && typeof global.AppWebBridge.getAppVersion === 'function') {
      return global.AppWebBridge.getAppVersion();
    }
    if (typeof global.getAppVersion === 'function') {
      return global.getAppVersion();
    }
    return fetchAppInfo().then(function (info) {
      return info && info.app_version != null ? info.app_version : null;
    });
  }

  function getAppInfoUnified() {
    return resolveProtocolTier().then(function (tier) {
      if (tier === 'r66' && global.AppWebBridge && global.AppWebBridge.isBridgeReady()) {
        return global.AppWebBridge.getAppInfo();
      }
      if (typeof global.getAppInfo === 'function') {
        return global.getAppInfo();
      }
      return Promise.reject(new Error('NO_GET_APP_INFO'));
    });
  }

  function isAppVersionAtLeastAsync(minVersion) {
    return getAppVersion().then(function (v) {
      if (v == null) return false;
      return compareVersion(v, minVersion);
    });
  }

  /** getMallTokenV2：id 仅从当前 URL 读取，无则不下发 id */
  function getMallTokenV2IdFromUrl() {
    try {
      var raw = new URLSearchParams(global.location.search).get('id');
      if (raw != null && raw !== '') {
        var n = parseInt(raw, 10);
        if (!isNaN(n)) return n;
      }
    } catch (e) {}
    return null;
  }

  function buildMallTokenV2InvokeParams(opts) {
    var p =
      opts && opts.params != null && typeof opts.params === 'object'
        ? Object.assign({}, opts.params)
        : {};
    var urlId = getMallTokenV2IdFromUrl();
    if (urlId != null) {
      p.id = urlId;
    } else {
      delete p.id;
    }
    return p;
  }

  var invokeHandlers = {
    getUserInfo: {
      legacy: function () {
        return global.getUserInfo();
      },
      r66: function () {
        return global.AppWebBridge.getUserInfo();
      },
    },
    getAppInfo: {
      legacy: function () {
        return global.getAppInfo();
      },
      r66: function () {
        return global.AppWebBridge.getAppInfo();
      },
    },
    getAppMallToken: {
      legacy: function () {
        return global.getAppMallToken();
      },
      r66: function (opts) {
        if (opts && opts.useV2) {
          return global.AppWebBridge.getMallTokenV2(buildMallTokenV2InvokeParams(opts));
        }
        if (opts && opts.params != null) {
          return global.AppWebBridge.getMallToken(opts.params);
        }
        return global.AppWebBridge.getAppMallToken();
      },
    },
    hideAppTabBar: {
      legacy: function (hide) {
        global.hideAppTabBar(hide !== false);
        return Promise.resolve({ hidden: hide !== false });
      },
      r66: function (hide) {
        return global.AppWebBridge.toggleTabBarVisible(hide === false);
      },
    },
    showAppTabBar: {
      legacy: function () {
        if (typeof global.showAppTabBar === 'function') {
          global.showAppTabBar();
        } else {
          global.hideAppTabBar(false);
        }
        return Promise.resolve({ visible: true });
      },
      r66: function () {
        return global.AppWebBridge.toggleTabBarVisible(true);
      },
    },
    closeCurrentPage: {
      legacy: function () {
        global.closeCurrentPage();
        return Promise.resolve({ called: true });
      },
      r66: function () {
        global.AppWebBridge.goBack();
        return Promise.resolve({ called: true });
      },
    },
    getStatusBarHeight: {
      legacy: function () {
        return global.getStatusBarHeight();
      },
      r66: function () {
        return global.AppWebBridge.getStatusBarHeight();
      },
    },
  };

  function invoke(method) {
    var args = Array.prototype.slice.call(arguments, 1);
    return resolveProtocolTier().then(function (tier) {
      var handler = invokeHandlers[method];
      if (!handler) {
        if (tier === 'r66' && global.AppWebBridge && typeof global.AppWebBridge[method] === 'function') {
          return global.AppWebBridge[method].apply(global.AppWebBridge, args);
        }
        if (typeof global[method] === 'function') {
          return Promise.resolve(global[method].apply(global, args));
        }
        return Promise.reject(new Error('UNKNOWN_METHOD: ' + method));
      }
      var fn = tier === 'r66' ? handler.r66 : handler.legacy;
      if (!fn) {
        return Promise.reject(new Error('NOT_IMPLEMENTED_FOR_TIER: ' + method + '/' + tier));
      }
      if (tier === 'r66' && (!global.AppWebBridge || !global.AppWebBridge.isBridgeReady())) {
        return Promise.reject(new Error('R66_BRIDGE_NOT_READY'));
      }
      return Promise.resolve(fn.apply(null, args));
    });
  }

  function resetProtocolTierCache() {
    _tierCache = null;
    _tierReasonCache = null;
    _tierPromise = null;
  }

  var AppBridge = {
    PROTOCOL_APILEVEL: PROTOCOL_APILEVEL,
    PROTOCOL_MIN_APP_VERSION: PROTOCOL_MIN_APP_VERSION,
    FEATURE_COMPACT_NAV_MIN_VERSION: FEATURE_COMPACT_NAV_MIN_VERSION,
    hasUrlApiLevel40660: hasUrlApiLevel40660,
    resetProtocolTierCache: resetProtocolTierCache,
    getProtocolTier: resolveProtocolTier,
    getProtocolTierSync: getProtocolTierSync,
    getProtocolTierReason: getProtocolTierReason,
    getAppVersion: getAppVersion,
    getAppInfo: getAppInfoUnified,
    isAppVersionAtLeastAsync: isAppVersionAtLeastAsync,
    invoke: invoke,
    features: {
      compactNavEnabled: function () {
        if (global.AppWebBridge && typeof global.AppWebBridge.isAppVersionAtLeast === 'function') {
          return global.AppWebBridge.isAppVersionAtLeast(FEATURE_COMPACT_NAV_MIN_VERSION);
        }
        return isAppVersionAtLeastAsync(FEATURE_COMPACT_NAV_MIN_VERSION);
      },
    },
  };

  global.AppBridge = AppBridge;
})(typeof window !== 'undefined' ? window : this);
